import { state } from "./state";
import { escapeHtml, escapeAttr, kindEmoji, byId } from "./dom";
import { filteredItems } from "./search";
import { isLibrary, groupPrefabs } from "./model";
import type { Asset, Prefab, PrefabGroup } from "./model";
import { selectAsset, selectPrefab, setHash } from "./router";
import { renderFilters, renderGrid } from "./render";

export function renderGridImpl(): void {
  const items = filteredItems();
  const grid = byId("grid");
  if (items.length === 0) {
    grid.innerHTML = `<div class="empty">no matches</div>`;
    return;
  }
  if (state.view === "assets") {
    grid.innerHTML = (items as Asset[]).map(assetCard).join("");
  } else {
    grid.innerHTML = groupPrefabs(items as Prefab[]).map(prefabCard).join("");
  }
  grid.querySelectorAll(".card").forEach((cardEl) => {
    cardEl.addEventListener("click", () => {
      const card = cardEl as HTMLElement;
      if (card.dataset.kind === "asset") {
        selectAsset(card.dataset.prefab!, card.dataset.id!);
      } else {
        // Card body opens the latest revision (null version → latest).
        selectPrefab(card.dataset.id!, null);
      }
    });
  });
  bindRevisionControls(grid);
  bindTagPills(grid);
}

/** Wire revision pills/dropdowns without triggering the card-body handler. */
function bindRevisionControls(root: HTMLElement): void {
  root.querySelectorAll(".rev-pill").forEach((pillEl) => {
    pillEl.addEventListener("click", (e) => {
      e.stopPropagation();
      const el = pillEl as HTMLElement;
      selectPrefab(el.dataset.prefabId!, el.dataset.revVersion!);
    });
  });
  root.querySelectorAll<HTMLSelectElement>(".rev-select").forEach((sel) => {
    sel.addEventListener("click", (e) => e.stopPropagation());
    sel.addEventListener("change", (e) => {
      e.stopPropagation();
      selectPrefab(sel.dataset.prefabId!, sel.value);
    });
  });
}

export function bindTagPills(root: HTMLElement): void {
  root.querySelectorAll(".pill-tag").forEach((pillEl) => {
    pillEl.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleTagFilter((pillEl as HTMLElement).dataset.tag!);
    });
  });
}

function assetCard(a: Asset): string {
  const isSelected =
    state.selectedAsset &&
    state.selectedAsset.prefab === a.prefab &&
    state.selectedAsset.id === a.id;
  const thumb = a.thumbnail
    ? `<img src="${escapeAttr(a.thumbnail)}" loading="lazy" alt="${escapeAttr(a.id)}"/>`
    : `<span class="placeholder">${kindEmoji(a.kind)}</span>`;
  const tagPills = (a.tags || [])
    .slice(0, 3)
    .map((t) => tagPillHtml(t))
    .join("");
  const dimsPill =
    a.width != null && a.height != null
      ? `<span class="pill dims">${a.width}×${a.height}${a.frame_count != null && a.frame_count > 1 ? ` · ${a.frame_count}f` : ""}</span>`
      : "";
  return `<div class="card${isSelected ? " selected" : ""}" data-kind="asset" data-prefab="${escapeAttr(a.prefab)}" data-id="${escapeAttr(a.id)}">
    <div class="thumb">${thumb}</div>
    <div class="name">${escapeHtml(a.id)}</div>
    <div class="meta"><span class="pill kind">${escapeHtml(a.kind)}</span>${dimsPill}${tagPills}</div>
  </div>`;
}

function prefabCard(g: PrefabGroup): string {
  const p = g.latest;
  const isSelected = state.selectedPrefab && state.selectedPrefab.id === g.id;
  const library = isLibrary(p);
  const placeholderGlyph = library ? "📚" : "📦";
  const thumb = p.thumbnail
    ? `<img src="${escapeAttr(p.thumbnail)}" loading="lazy" alt="${escapeAttr(p.id)}" onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'placeholder',textContent:'${placeholderGlyph}'}))"/>`
    : `<span class="placeholder">${placeholderGlyph}</span>`;
  const tagPills = (p.tags || [])
    .slice(0, 3)
    .map((t) => tagPillHtml(t))
    .join("");
  const libraryBadge = library
    ? `<span class="library-badge">library</span>`
    : "";
  const countPill = library
    ? `<span class="pill">README</span>`
    : `<span class="pill">${p.asset_count} assets</span>`;
  return `<div class="card${isSelected ? " selected" : ""}" data-kind="prefab" data-id="${escapeAttr(g.id)}">
    <div class="thumb">${thumb}</div>
    <div class="name">${escapeHtml(g.id)}${libraryBadge}</div>
    <div class="meta">${countPill}${tagPills}</div>
    ${revisionsHtml(g)}
  </div>`;
}

/** Pills for 1–3 revisions, a dropdown for 4+, nothing for a lone revision. */
function revisionsHtml(g: PrefabGroup): string {
  if (g.revisions.length < 2) return "";
  // Which revision is currently open in the detail panel (null → latest).
  const current =
    state.selectedPrefab && state.selectedPrefab.id === g.id
      ? (state.selectedPrefab.version ?? g.latest.version)
      : null;
  if (g.revisions.length <= 3) {
    const pills = g.revisions
      .map((r) => {
        const latest = r.version === g.latest.version ? " latest" : "";
        const active = r.version === current ? " active" : "";
        return `<button class="pill rev-pill${latest}${active}" type="button" data-prefab-id="${escapeAttr(g.id)}" data-rev-version="${escapeAttr(r.version)}">${escapeHtml(r.version)}</button>`;
      })
      .join("");
    return `<div class="revs"><span class="revs-label">revs</span>${pills}</div>`;
  }
  const opts = g.revisions
    .map((r) => {
      const isLatest = r.version === g.latest.version;
      const selected =
        r.version === current || (current === null && isLatest)
          ? " selected"
          : "";
      return `<option value="${escapeAttr(r.version)}"${selected}>${escapeHtml(r.version)}${isLatest ? " (latest)" : ""}</option>`;
    })
    .join("");
  return `<select class="rev-select" data-prefab-id="${escapeAttr(g.id)}">${opts}</select>`;
}

export function tagPillHtml(tag: string): string {
  const active = state.filters.tags.has(tag) ? " active" : "";
  return `<button class="pill pill-tag${active}" type="button" data-tag="${escapeAttr(tag)}">${escapeHtml(tag)}</button>`;
}

function toggleTagFilter(tag: string): void {
  if (state.view !== "assets") {
    state.view = "assets";
    syncViewToggleButtons();
    state.filters.tags.add(tag);
    state.selectedPrefab = null;
    setHash("");
    const detail = byId("detail");
    detail.hidden = true;
    detail.innerHTML = "";
  } else {
    const set = state.filters.tags;
    if (set.has(tag)) set.delete(tag);
    else set.add(tag);
  }
  renderFilters();
  renderGrid();
}

export function syncViewToggleButtons(): void {
  byId("view-toggle")
    .querySelectorAll("button[data-view]")
    .forEach((bEl) => {
      const b = bEl as HTMLElement;
      b.classList.toggle("active", b.dataset.view === state.view);
    });
}

export function highlightSelectedCard(): void {
  byId("grid")
    .querySelectorAll(".card")
    .forEach((cardEl) => {
      const card = cardEl as HTMLElement;
      let match = false;
      if (state.selectedAsset && card.dataset.kind === "asset") {
        match =
          card.dataset.prefab === state.selectedAsset.prefab &&
          card.dataset.id === state.selectedAsset.id;
      } else if (state.selectedPrefab && card.dataset.kind === "prefab") {
        match = card.dataset.id === state.selectedPrefab.id;
      }
      card.classList.toggle("selected", match);
    });
}
